import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Section, ghostButton, primaryButton } from "@/components/settings/primitives";
import { Toggle } from "@/components/settings/TaskSettings";
import { ROLES, TextField, TimezoneField, ToneField, WorkHoursField } from "@/components/settings/PersonalizationFields";
import { api } from "@/lib/api";
import { useUpdateSettings } from "@/hooks/queries";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const FIELDS = ["enabled", "role", "industry", "goals", "tone", "preferences", "work_start", "work_end", "work_days"];
const pick = (d) => Object.fromEntries(FIELDS.map((k) => [k, d[k]]));

export function PersonalizationSection({ me }) {
  const qc = useQueryClient();
  const settings = useUpdateSettings();
  const query = useQuery({ queryKey: ["personalization"], queryFn: () => api.get("/me/personalization") });
  const [form, setForm] = useState(null);
  useEffect(() => { if (query.data) setForm(pick(query.data)); }, [query.data]);
  const done = (data) => qc.setQueryData(["personalization"], data);
  const save = useMutation({ mutationFn: (body) => api.put("/me/personalization", body), onSuccess: (d) => { done(d); toast.success("Saved. JARVIS will use this from your next message."); }, onError: (e) => toast.error(e.message) });
  const wipe = useMutation({ mutationFn: () => api.del("/me/personalization"), onSuccess: () => { qc.invalidateQueries({ queryKey: ["personalization"] }); toast.success("Your saved details were deleted."); }, onError: (e) => toast.error(e.message) });
  if (!form) return <div className="h-48 animate-pulse rounded-xl bg-white/[0.04]" />;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const hours = Boolean(form.work_start) === Boolean(form.work_end);

  return (
    <div className="space-y-4">
      <Section title="About you" description="Only you can see this. JARVIS uses it to make answers, examples and plans relevant to you — in Chat and in voice." testId="settings-about-section">
        <div className="space-y-5">
          <Toggle label="Use this to personalise replies" hint="Turn off anytime without deleting what you saved." checked={form.enabled} onChange={(enabled) => set({ enabled })} testId="about-enabled-switch" />
          <datalist id="about-roles">{ROLES.map((r) => <option key={r} value={r} />)}</datalist>
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField label="Your role" value={form.role} onChange={(role) => set({ role })} max={80} list="about-roles" testId="about-role-input" />
            <TextField label="Industry" value={form.industry} onChange={(industry) => set({ industry })} max={80} testId="about-industry-input" />
          </div>
          <TextField label="Current goals" hint="For example: launch my bakery's online store by March." value={form.goals} onChange={(goals) => set({ goals })} max={600} multiline testId="about-goals-input" />
          <ToneField value={form.tone} onChange={(tone) => set({ tone })} />
          <TextField label="Anything else about how you like answers" value={form.preferences} onChange={(preferences) => set({ preferences })} max={400} multiline testId="about-preferences-input" />
          <WorkHoursField form={form} set={set} />
          <TimezoneField value={me.settings.timezone} onChange={(timezone) => settings.mutate({ timezone }, { onError: (e) => toast.error(e.message) })} />
          {!hours && <p className="text-xs text-rose-300" data-testid="about-hours-error">Add both a start and an end time, or clear both.</p>}
          <div className="flex flex-wrap gap-2 pt-1">
            <button type="button" className={primaryButton} disabled={save.isPending || !hours} onClick={() => save.mutate(form)} data-testid="about-save-button">{save.isPending ? "Saving…" : "Save"}</button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button type="button" className={ghostButton} disabled={!query.data?.saved || wipe.isPending} data-testid="about-delete-button">Delete my details</button>
              </AlertDialogTrigger>
              <AlertDialogContent className="border-white/10 bg-ink-850">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white">Delete your saved details?</AlertDialogTitle>
                  <AlertDialogDescription>Your role, goals, preferences and working hours are removed permanently. Your time zone setting stays.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="about-delete-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => wipe.mutate()} data-testid="about-delete-confirm">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </Section>
    </div>
  );
}
